import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import type { TSpaceParticipantCandidate } from '@onesource/schemas';
import { getMediaUrl, useAssignReviewTeacher, useGetReviewTeachers } from '@onesource/services';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect, useRef, useState } from 'react';
import { MdClose, MdPersonAdd } from 'react-icons/md';

interface AssignTeacherDialogProps {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  submissionId: string;
  studentName: string;
}

/**
 * Admin: give a student with no teacher a teacher, from their submission. It is
 * the same assignment as on the dashboard's Teachers & Students page.
 */
export const AssignTeacherDialog: FC<AssignTeacherDialogProps> = ({
  open,
  onClose,
  spaceId,
  submissionId,
  studentName,
}) => {
  const [teacher, setTeacher] = useState<TSpaceParticipantCandidate | null>(null);
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const assign = useAssignReviewTeacher();
  const { data: teachers = [], isFetching, error } = useGetReviewTeachers(spaceId, search, open);

  // Debounce the typed name before it drives the server search.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    if (open) {
      setTeacher(null);
      setInput('');
      assign.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAssign = async () => {
    if (!teacher) {
      return;
    }
    try {
      await assign.mutateAsync({ spaceId, submissionId, teacherUserUuid: teacher.userUuid });
      enqueueSnackbar(`${teacher.name} is now ${studentName}'s teacher`, { variant: 'success' });
      onClose();
    } catch {
      // Shown in the dialog.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={assign.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // autoFocus loses to the dialog's own focus trap; focus once the dialog is in.
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
    >
      <DialogTitle
        onClose={onClose}
        icon={<MdPersonAdd size={20} />}
        subtitle={`${studentName} has no teacher in this program yet.`}
      >
        Assign teacher
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Autocomplete
          value={teacher}
          onChange={(_event, value) => setTeacher(value)}
          inputValue={input}
          onInputChange={(_event, value) => setInput(value)}
          options={teachers}
          filterOptions={options => options}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option.userUuid === value.userUuid}
          loading={isFetching}
          noOptionsText={search ? 'No teacher matches this name' : 'No teachers in this program'}
          renderOption={({ key, ...props }, option) => (
            <Box component="li" key={key} {...props} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Avatar src={option.avatar ? getMediaUrl(option.avatar) : undefined} sx={{ width: 32, height: 32 }}>
                {option.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {option.name}
                </Typography>
                {option.email && (
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {option.email}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              inputRef={inputRef}
              label="Teacher"
              placeholder="Search by name or email"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isFetching && <CircularProgress size={16} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This teacher reviews this submission. {studentName}&apos;s other Hands-On Practice submissions in this program
          that wait for a review also go to this teacher, and so do new ones.
        </Typography>
        {(assign.error || error) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(assign.error ?? error)?.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="soft" startIcon={<MdClose />} onClick={onClose} disabled={assign.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!teacher || assign.isPending}
          startIcon={assign.isPending ? <CircularProgress size={16} color="inherit" /> : <MdPersonAdd />}
          onClick={handleAssign}
        >
          Assign teacher
        </Button>
      </DialogActions>
    </Dialog>
  );
};
